/**
 * The three overlaps the ambient-POI layer shipped without resolving, each one
 * wired to the fix it names so the canvas answers the question instead of a
 * panel row asserting it.
 *
 * They are not variants of one another — each is a different defect in a
 * different place — so this axis reads as "which of the three is worth the
 * cost", and only one fix is live at a time. That is deliberate: two of them
 * trade something away (a reserved pin footprint costs ambient labels, a denser
 * card costs the Ethereal Glass material, a floored fit costs pins outside the
 * frame), and a reader judging three simultaneous trades cannot tell which one
 * they are looking at.
 *
 * Each row is also its own control for the other two: `UnderPins` and
 * `ThroughCards` leave the camera alone, so flipping to `ZoomFloor` shows the
 * fit change against them; and neither of those two touches the other's
 * surface, so each is the before-state for its sibling.
 *
 * Kept DOM-free at module scope — `document` is touched only inside
 * `ensurePinReservationImage` — so the shape can be asserted without a browser.
 */

type MapLibreModule = typeof import('maplibre-gl');
type MapLibreMapInstance = InstanceType<MapLibreModule['Map']>;
type SymbolLayer = import('maplibre-gl').SymbolLayerSpecification;
type PointFeatureCollection = import('geojson').FeatureCollection<
  import('geojson').Point
>;

/** Which of the three overlaps is being resolved on the canvas right now. */
export enum MapPoiFollowUp {
  /**
   * Ambient labels can be placed beneath an authored pin, because MapLibre's
   * collision index sees canvas symbols and the pins are DOM markers — a whole
   * layer the index has no knowledge of.
   */
  UnderPins = 'Labels under pins',
  /**
   * The rail card is translucent, so ambient names beneath it stay legible
   * through the glass and read as text inside the card that is not there.
   */
  ThroughCards = 'Labels through cards',
  /**
   * A multi-pin block fits below the tiles' z14 place floor, so it opens on a
   * frame where ambient POIs provably cannot draw.
   */
  ZoomFloor = 'No places until you zoom',
}

export const MAP_POI_FOLLOW_UPS: readonly MapPoiFollowUp[] = [
  MapPoiFollowUp.UnderPins,
  MapPoiFollowUp.ThroughCards,
  MapPoiFollowUp.ZoomFloor,
];

/** Row copy. The enum value stays the short form because it is the persistence
 *  identity; rewording a label must never orphan a selection already made. */
export const MAP_POI_FOLLOW_UP_LABEL: Record<MapPoiFollowUp, string> = {
  [MapPoiFollowUp.UnderPins]: 'Pins reserve their own footprint',
  [MapPoiFollowUp.ThroughCards]: 'Rail cards stop the map showing through',
  [MapPoiFollowUp.ZoomFloor]: 'A block never opens below the place floor',
};

/**
 * What a map with no control over it resolves: NONE of them.
 *
 * `null` and not a member, because these three are candidate fixes under
 * evaluation and every one of them spends something — ambient labels, the glass
 * material, pins outside the frame. The provider is mounted by exactly one
 * surface, the dev bench; transcript fences and result-card maps mount none, so
 * a member here would ship an unratified change to every map in the product as
 * a side effect of building a control. The context's own contract is that a
 * provider-less embedder keeps the baked picks, and this keeps it.
 */
export const MAP_POI_FOLLOW_UP_DEFAULT: MapPoiFollowUp | null = null;

/** Whether authored pins claim their footprint against the collision index. */
export function followUpReservesPins(followUp: MapPoiFollowUp | null): boolean {
  return followUp === MapPoiFollowUp.UnderPins;
}

/** Whether the FITTED frame is floored at the ambient place zoom. The intro
 *  flight still departs from its own start zoom; only where it lands moves. */
export function followUpFloorsZoom(followUp: MapPoiFollowUp | null): boolean {
  return followUp === MapPoiFollowUp.ZoomFloor;
}

/* ── The pin footprint reservation ────────────────────────────────────────── */

export const MAP_PIN_RESERVATION_SOURCE_ID = 'cabal-pin-reservation';
export const MAP_PIN_RESERVATION_LAYER_ID = 'cabal-pin-reservation';
const PIN_RESERVATION_IMAGE_ID = 'cabal-pin-reservation-box';

/**
 * The reserved box, in CSS pixels — and the pixel ratio is 1 for that reason.
 *
 * MapLibre lays an image out at `displaySize = size / pixelRatio`
 * (`image_atlas.ts`), so registering a 44px bitmap at ratio 2 reserves 22px:
 * SMALLER than the pin it is meant to cover, which is the opposite of the job.
 * The dots elsewhere in this widget use ratio 2 because they are DRAWN and want
 * retina crispness; this image is never drawn, so only its box matters.
 *
 * 44 covers the marker's real footprint: the `.map-poi` element is 30px square
 * and the Terrace recut rotates its teardrop -45°, which carries the corner out
 * to roughly 42px across.
 */
const PIN_RESERVATION_SIZE = 44;
const PIN_RESERVATION_PIXEL_RATIO = 1;
const CANVAS_2D_CONTEXT = '2d';

const LAYER_TYPE_SYMBOL = 'symbol';
const SOURCE_TYPE_GEOJSON = 'geojson';
/* The GeoJSON discriminants, annotated with their literal types rather than
   widened to `string`: the spec types require the literal, and a widened const
   makes the collection unassignable to the source MapLibre accepts. */
const FEATURE_COLLECTION: 'FeatureCollection' = 'FeatureCollection';
const FEATURE: 'Feature' = 'Feature';
const GEOMETRY_POINT: 'Point' = 'Point';

/** One authored pin, reduced to the only thing a reservation needs. */
export interface PinReservationPoint {
  readonly lat: number;
  readonly lon: number;
}

export function buildPinReservationData(
  points: readonly PinReservationPoint[],
): PointFeatureCollection {
  return {
    type: FEATURE_COLLECTION,
    features: points.map((point) => ({
      type: FEATURE,
      geometry: { type: GEOMETRY_POINT, coordinates: [point.lon, point.lat] },
      properties: {},
    })),
  };
}

/**
 * The layer that does the reserving.
 *
 * It draws nothing — the image is fully transparent — but a symbol layer's
 * collision box comes from its icon's DIMENSIONS and not from a single opaque
 * pixel, so an invisible icon occupies the index exactly as a visible one does.
 *
 * The two placement flags do different jobs and the pairing matters:
 *
 *   `icon-allow-overlap: true`  — "visible even if it collides with other
 *     previously drawn symbols". This is what makes the reservation itself
 *     unrejectable. At `false` the reservations collide with EACH OTHER, so two
 *     close pins leave one of them reserving nothing at all.
 *   `icon-ignore-placement: false` — "other symbols can be visible even if they
 *     collide" is what `true` would mean. Left at false, so this box is what
 *     blocks the labels underneath. It is the half that does the reserving.
 *
 * The cost, stated: appended above the whole style, this refuses the space to
 * any lower symbol near a pin, not to ambient POIs alone — a street name that
 * would have printed across a pin gives way too.
 */
export function buildPinReservationLayer(): SymbolLayer {
  return {
    id: MAP_PIN_RESERVATION_LAYER_ID,
    type: LAYER_TYPE_SYMBOL,
    source: MAP_PIN_RESERVATION_SOURCE_ID,
    layout: {
      'icon-image': PIN_RESERVATION_IMAGE_ID,
      'icon-allow-overlap': true,
      'icon-ignore-placement': false,
      'icon-size': 1,
    },
    paint: { 'icon-opacity': 0 },
  } as SymbolLayer;
}

function reservationImageData(): ImageData | null {
  const canvas = document.createElement('canvas');
  const size = PIN_RESERVATION_SIZE;
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext(CANVAS_2D_CONTEXT);
  if (!context) {
    return null;
  }

  // Left as the transparent black every fresh canvas starts at: the box is
  // what matters, never the pixels.
  return context.getImageData(0, 0, size, size);
}

function ensurePinReservationImage(map: MapLibreMapInstance): void {
  if (map.hasImage(PIN_RESERVATION_IMAGE_ID)) {
    return;
  }
  const data = reservationImageData();
  if (!data) {
    return;
  }
  map.addImage(PIN_RESERVATION_IMAGE_ID, data, {
    pixelRatio: PIN_RESERVATION_PIXEL_RATIO,
  });
}

/**
 * Every style mutation here goes through this, and it is not defensive
 * decoration.
 *
 * `addSource` and `addLayer` call MapLibre's `_checkLoaded`, which THROWS
 * `Style is not done loading` rather than returning a failure — and this runs
 * from an effect that fires before the style has parsed as well as from the
 * `style.load` hook after it. An escaping throw does not merely skip the layer:
 * it propagates out of the effect and takes the whole React tree down to the
 * error boundary, which is a blank page where a map was.
 */
function tryStyleWrite(apply: () => void): void {
  try {
    apply();
  } catch {
    // The style is mid-parse or mid-swap; the `style.load` pass settles it.
  }
}

export function removeMapPinReservation(map: MapLibreMapInstance): void {
  tryStyleWrite(() => {
    if (map.getLayer(MAP_PIN_RESERVATION_LAYER_ID)) {
      map.removeLayer(MAP_PIN_RESERVATION_LAYER_ID);
    }
    if (map.getSource(MAP_PIN_RESERVATION_SOURCE_ID)) {
      map.removeSource(MAP_PIN_RESERVATION_SOURCE_ID);
    }
  });
}

/**
 * Applies (or clears) the reservation.
 *
 * Idempotent and re-runnable, like `applyMapAmbientPoi`: it removes its own
 * layer and source first, so a variant flip, a late-parsing style and a second
 * call on a settled map all converge on one layer.
 *
 * Appended on TOP of the style rather than anchored, and that is the whole
 * mechanism: MapLibre runs placement from the topmost symbol layer down, so a
 * reservation above every label is placed FIRST and every ambient name that
 * would have landed inside a pin's footprint is refused the space.
 */
export interface PinReservationInput {
  readonly map: MapLibreMapInstance;
  readonly enabled: boolean;
  readonly points: readonly PinReservationPoint[];
}

export function applyMapPinReservation({
  map,
  enabled,
  points,
}: PinReservationInput): void {
  removeMapPinReservation(map);
  if (!enabled || points.length === 0) {
    return;
  }

  // A style that has not parsed carries no layers to place against, and asking
  // it for a source is the throw described above. The `style.load` pass is what
  // covers this case, so returning here loses nothing.
  if (!map.isStyleLoaded()) {
    return;
  }

  tryStyleWrite(() => {
    ensurePinReservationImage(map);
    map.addSource(MAP_PIN_RESERVATION_SOURCE_ID, {
      type: SOURCE_TYPE_GEOJSON,
      data: buildPinReservationData(points),
    });
    map.addLayer(buildPinReservationLayer());
  });
}
